import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

export interface DemandExampleTemplate {
  id: string;
  demand_example: string;
  name: string;
  description?: string | null;
  created_at: string;
  modified_at: string;
  sections_count?: number;
}

export interface DemandExampleTemplateSection {
  id: string;
  template: string;
  section_name: string;
  description?: string | null;
  order: number;
  image_url?: string | null;
  bounding_boxes?: any;
  block_ids?: any;
  assigned_rule_ids?: string[];
  confidence?: number | null;
  created_at: string;
  updated_at: string;
}

export const demandExampleTemplateApi = createApi({
  reducerPath: 'demandExampleTemplateApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'DemandExampleTemplates',
    'DemandExampleTemplateSections',
  ] as const,
  endpoints: (builder) => ({
    listTemplates: builder.query<
      DemandExampleTemplate[],
      { demandExampleId: string }
    >({
      query: ({ demandExampleId }) =>
        `api/demand-examples/${demandExampleId}/templates/`,
      providesTags: (result) =>
        result
          ? [
              ...result.map((tpl) => ({
                type: 'DemandExampleTemplates' as const,
                id: tpl.id,
              })),
              { type: 'DemandExampleTemplates', id: 'LIST' },
            ]
          : [{ type: 'DemandExampleTemplates', id: 'LIST' }],
    }),

    createTemplate: builder.mutation<
      DemandExampleTemplate,
      {
        demandExampleId: string;
        payload: {
          name: string;
          description?: string;
          demand_example?: string;
        };
      }
    >({
      query: ({ demandExampleId, payload }) => ({
        url: `api/demand-examples/${demandExampleId}/templates/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'DemandExampleTemplates', id: 'LIST' }],
    }),

    deleteTemplate: builder.mutation<
      void,
      { demandExampleId: string; templateId: string }
    >({
      query: ({ demandExampleId, templateId }) => ({
        url: `api/demand-examples/${demandExampleId}/templates/${templateId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (r, e, { templateId }) => [
        { type: 'DemandExampleTemplates', id: templateId },
        { type: 'DemandExampleTemplates', id: 'LIST' },
      ],
    }),

    updateTemplate: builder.mutation<
      DemandExampleTemplate,
      {
        demandExampleId: string;
        templateId: string;
        payload: { name?: string; description?: string };
      }
    >({
      query: ({ demandExampleId, templateId, payload }) => ({
        url: `api/demand-examples/${demandExampleId}/templates/${templateId}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (r, e, { templateId }) => [
        { type: 'DemandExampleTemplates', id: templateId },
        { type: 'DemandExampleTemplates', id: 'LIST' },
      ],
    }),

    // Sections
    listSections: builder.query<
      DemandExampleTemplateSection[],
      { demandExampleId: string; templateId: string }
    >({
      query: ({ demandExampleId, templateId }) =>
        `api/demand-examples/${demandExampleId}/templates/${templateId}/sections/`,
      providesTags: (result) =>
        result
          ? [
              ...result.map((sec) => ({
                type: 'DemandExampleTemplateSections' as const,
                id: sec.id,
              })),
              { type: 'DemandExampleTemplateSections', id: 'LIST' },
            ]
          : [{ type: 'DemandExampleTemplateSections', id: 'LIST' }],
    }),

    createSection: builder.mutation<
      DemandExampleTemplateSection,
      { demandExampleId: string; templateId: string; formData: FormData }
    >({
      query: ({ demandExampleId, templateId, formData }) => ({
        url: `api/demand-examples/${demandExampleId}/templates/${templateId}/sections/`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: [{ type: 'DemandExampleTemplateSections', id: 'LIST' }],
    }),

    // Regenerate section thumbnails on the backend
    regenerateImages: builder.mutation<
      void,
      { demandExampleId: string; templateId: string }
    >({
      query: ({ demandExampleId, templateId }) => ({
        url: `api/demand-examples/${demandExampleId}/templates/${templateId}/regenerate-images/`,
        method: 'POST',
      }),
      invalidatesTags: (r, e, { templateId }) => [
        { type: 'DemandExampleTemplateSections', id: 'LIST' },
        { type: 'DemandExampleTemplates', id: templateId },
      ],
    }),

    // Delete a single section
    deleteSection: builder.mutation<
      void,
      { demandExampleId: string; templateId: string; sectionId: string }
    >({
      query: ({ demandExampleId, templateId, sectionId }) => ({
        url: `api/demand-examples/${demandExampleId}/templates/${templateId}/sections/${sectionId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'DemandExampleTemplateSections', id: 'LIST' }],
    }),

    getTemplate: builder.query<
      DemandExampleTemplate,
      { demandExampleId: string; templateId: string }
    >({
      query: ({ demandExampleId, templateId }) =>
        `api/demand-examples/${demandExampleId}/templates/${templateId}/`,
      providesTags: (r) => [{ type: 'DemandExampleTemplates', id: r?.id }],
    }),

    // Clone sections from analysis into a newly created template
    cloneFromAnalysis: builder.mutation<
      DemandExampleTemplateSection[],
      { demandExampleId: string; templateId: string }
    >({
      query: ({ demandExampleId, templateId }) => ({
        url: `api/demand-examples/${demandExampleId}/templates/${templateId}/clone-from-analysis/`,
        method: 'POST',
      }),
      invalidatesTags: (r, e, { templateId }) => [
        { type: 'DemandExampleTemplates', id: templateId },
        { type: 'DemandExampleTemplateSections', id: 'LIST' },
      ],
    }),

    duplicateTemplate: builder.mutation<
      DemandExampleTemplate,
      { demandExampleId: string; templateId: string }
    >({
      query: ({ demandExampleId, templateId }) => ({
        url: `api/demand-examples/${demandExampleId}/templates/${templateId}/duplicate/`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'DemandExampleTemplates', id: 'LIST' }],
    }),
  }),
});

export const {
  useListTemplatesQuery,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useListSectionsQuery,
  useCreateSectionMutation,
  useRegenerateImagesMutation,
  useUpdateTemplateMutation,
  useDeleteSectionMutation,
  useGetTemplateQuery,
  useCloneFromAnalysisMutation,
  useDuplicateTemplateMutation,
} = demandExampleTemplateApi;
